import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, Box, Radar, Cpu, Coins, Download, Share2, History, Save, Trash2, FileDown, Globe, Crosshair, Loader2, HelpCircle } from "lucide-react";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { jsPDF } from "jspdf";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useLang, useAuth } from "@/App";
import GuidedTour from "@/components/GuidedTour";
import PSeQ2XrButton from "@/components/PSeQ2XrButton";

const PSEQ2_TOUR_KEY = "dekrbat_pseq2_tour_v1";
// guided tour spotlight targets, one per translated step
const PSEQ2_TOUR_TARGETS = [
  null,
  '[data-testid="pseq2-config-bassins-slider"]',
  '[data-testid="pseq2-config-sun-select"]',
  '[data-testid="pseq2-map"]',
  '[data-testid="pseq2-cube-interactive"]',
  '[data-testid="pseq2-report-btn"]',
  '[data-testid="pseq2-share-btn"]',
  null,
];
import { Reveal, Overline, EASE } from "@/components/motion";
import { dictionary } from "@/i18n";

const API = process.env.REACT_APP_BACKEND_URL;
const BASSIN_M2 = 100; // unit surface: 100 m² per pond, divided into 4 independent 25 m² units (cross-contamination containment)
const TILE_Z = 14;
const BAR_COLORS = ["#7dd3fc", "#38bdf8", "#0ea5e9", "#0284c7"];

function tileCoords(lat, lng, z) {
  const n = 2 ** z;
  const x = Math.floor(((lng + 180) / 360) * n);
  const latR = (lat * Math.PI) / 180;
  const y = Math.floor(((1 - Math.log(Math.tan(latR) + 1 / Math.cos(latR)) / Math.PI) / 2) * n);
  return { x, y };
}

const tileUrl = (x, y, z) =>
  `https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/${z}/${y}/${x}`;

const SUN_FACTORS = { full: 1.15, mid: 1.0, low: 0.8 };
const RATING_COLORS = ["#f87171", "#fbbf24", "#fde047", "#4ade80", "#00E5A3"];

function scoreSite(w) {
  if (!w) return null;
  let s = 0;
  const t = w.temp;
  if (t >= 26 && t <= 30) s += 35;
  else if (t >= 22 && t <= 33) s += 25;
  else if (t >= 15 && t <= 36) s += 12;
  else s += 4;
  const h = w.sunshine;
  if (h >= 8) s += 30;
  else if (h >= 6) s += 22;
  else if (h >= 4) s += 14;
  else s += 6;
  const hm = w.humidity;
  if (hm >= 45 && hm <= 75) s += 20;
  else if (hm >= 30 && hm <= 85) s += 12;
  else s += 5;
  const pr = w.pressure;
  if (pr >= 1000 && pr <= 1025) s += 15;
  else if (pr >= 985 && pr <= 1035) s += 9;
  else s += 4;
  return Math.min(100, s);
}

function ratingIndex(score) {
  if (score == null) return -1;
  if (score < 30) return 0;
  if (score < 50) return 1;
  if (score < 65) return 2;
  if (score < 80) return 3;
  return 4;
}

const dotIcon = L.divIcon({
  className: "",
  html: '<div class="map-dot"></div>',
  iconSize: [16, 16],
  iconAnchor: [8, 8],
});

function ClickPicker({ onPick }) {
  useMapEvents({
    click(e) {
      onPick([e.latlng.lat, e.latlng.lng]);
    },
  });
  return null;
}

function FlyTo({ pos }) {
  const map = useMap();
  useEffect(() => {
    if (pos) map.flyTo(pos, Math.max(map.getZoom(), 6), { duration: 1.2 });
  }, [pos, map]);
  return null;
}

export function HoloCube({ value, unit, label, testid, size = 150 }) {
  return (
    <div className="flex flex-col items-center gap-5" data-testid={testid}>
      <div className="holo-float">
        <div className="holo-scene" style={{ "--cube": `${size}px` }}>
          <div className="holo-cube">
            <div className="holo-face f-front">
              <div className="px-2 text-center">
                <p className="font-mono text-2xl font-bold text-teal sm:text-3xl">{value}</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-ink/60">{unit}</p>
              </div>
            </div>
            <div className="holo-face f-back">
              <div className="px-2 text-center">
                <p className="font-mono text-2xl font-bold text-teal sm:text-3xl">{value}</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-ink/60">{unit}</p>
              </div>
            </div>
            <div className="holo-face f-right">
              <span className="holo-side-label">{label}</span>
            </div>
            <div className="holo-face f-left">
              <span className="holo-side-label">{label}</span>
            </div>
            <div className="holo-face f-top" />
            <div className="holo-face f-bottom" />
          </div>
        </div>
      </div>
      <div className="holo-pedestal" aria-hidden="true" />
      <p className="-mt-3 text-center font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-ink/70">
        {label}
      </p>
    </div>
  );
}

const lbCellLabel = "font-mono text-[9px] uppercase tracking-[0.22em] text-sky-200/70";
const lbCellVal = "font-mono text-lg font-bold text-white";

function MasterCube({ p, place, weather, weatherState, results, rIdx }) {
  const lb = "holo-face lb";
  const cubeRef = useRef(null);
  const rot = useRef({ rx: -16, ry: 0, dragging: false, px: 0, py: 0 });
  const [zoomed, setZoomed] = useState(false);
  const [pulse, setPulse] = useState(false);

  useEffect(() => {
    let raf;
    let prev = performance.now();
    const loop = (now) => {
      const s = rot.current;
      const dt = now - prev;
      prev = now;
      if (!s.dragging) s.ry += (dt / 16000) * 360;
      if (cubeRef.current) {
        cubeRef.current.style.transform = `rotateX(${s.rx}deg) rotateY(${s.ry}deg)`;
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  const onPointerDown = (e) => {
    const s = rot.current;
    s.dragging = true;
    s.px = e.clientX;
    s.py = e.clientY;
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e) => {
    const s = rot.current;
    if (!s.dragging) return;
    s.ry += (e.clientX - s.px) * 0.45;
    s.rx = Math.max(-70, Math.min(30, s.rx - (e.clientY - s.py) * 0.35));
    s.px = e.clientX;
    s.py = e.clientY;
  };

  const onPointerUp = () => {
    rot.current.dragging = false;
    setPulse(true);
    setTimeout(() => setPulse(false), 900);
  };

  return (
    <div className="flex flex-col items-center gap-6" data-testid="pseq2-weather-cube">
      <div className="holo-float">
        <div
          data-testid="pseq2-cube-interactive"
          className={`holo-interactive ${zoomed ? "holo-zoomed" : ""} ${pulse ? "holo-pulse" : ""}`}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onDoubleClick={() => setZoomed((z) => !z)}
        >
          <div className="holo-scene" style={{ "--cube": "min(240px, 60vw)" }}>
            <div ref={cubeRef} className="holo-cube" style={{ animation: "none" }}>
              <div className={`${lb} f-front`}>
                <div className="flex flex-col items-center gap-2 px-3 text-center">
                  {rIdx >= 0 && (
                    <span
                      data-testid="pseq2-rating-badge"
                      className="inline-flex items-center gap-2 rounded-full border px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.18em]"
                      style={{ borderColor: RATING_COLORS[rIdx], color: RATING_COLORS[rIdx] }}
                    >
                      <span className="h-2 w-2 rounded-full" style={{ background: RATING_COLORS[rIdx], boxShadow: `0 0 8px ${RATING_COLORS[rIdx]}` }} />
                      {p.rating.levels[rIdx]}
                    </span>
                  )}
                  <p className="font-mono text-4xl font-extrabold text-white">
                    {weatherState === "loading" ? "…" : results.pseq2}
                  </p>
                  <p className={lbCellLabel}>PSEQ2 · {p.cubes.pseq2.unit}</p>
                </div>
              </div>
              <div className={`${lb} f-back`}>
                <div className="flex flex-col items-center gap-1.5 px-3 text-center">
                  <p className={lbCellVal}>{weatherState === "loading" ? "…" : results.co2} <span className="text-[10px] font-normal text-sky-200/70">{p.cubes.co2.unit}</span></p>
                  <p className={lbCellLabel}>{p.cubes.co2.label}</p>
                  <p className={`${lbCellVal} mt-2`}>{weatherState === "loading" ? "…" : results.feed}</p>
                  <p className={lbCellLabel}>{p.cubes.feed.label}</p>
                  <p className={`${lbCellVal} mt-2`}>{weatherState === "loading" ? "…" : results.jobs}</p>
                  <p className={lbCellLabel}>{p.cubes.jobs.label}</p>
                </div>
              </div>
              <div className={`${lb} f-right`}>
                <div className="flex flex-col items-center gap-1.5 px-3 text-center">
                  <p className={lbCellVal}>{weather ? `${weather.temp.toFixed(1)}°C` : "--"}</p>
                  <p className={lbCellLabel}>{p.weather.temp}</p>
                  <p className={`${lbCellVal} mt-3`}>{weather ? `${weather.sunshine.toFixed(1)}${p.weather.hours}` : "--"}</p>
                  <p className={lbCellLabel}>{p.weather.sunshine}</p>
                </div>
              </div>
              <div className={`${lb} f-left`}>
                <div className="flex flex-col items-center gap-1.5 px-3 text-center">
                  <p className={lbCellVal}>{weather ? `${Math.round(weather.humidity)}%` : "--"}</p>
                  <p className={lbCellLabel}>{p.weather.humidity}</p>
                  <p className={`${lbCellVal} mt-3`}>{weather ? `${Math.round(weather.pressure)}` : "--"}</p>
                  <p className={lbCellLabel}>{p.weather.pressure} hPa</p>
                </div>
              </div>
              <div className={`${lb} f-top`}>
                <span className="px-3 text-center font-mono text-[10px] uppercase tracking-[0.18em] text-sky-100">
                  {place || "PSEQ2"}
                </span>
              </div>
              <div className={`${lb} f-bottom`} />
            </div>
          </div>
        </div>
      </div>
      <div
        className="holo-pedestal"
        style={{ background: "radial-gradient(ellipse, rgba(125,211,252,0.4) 0%, rgba(125,211,252,0) 70%)" }}
        aria-hidden="true"
      />
      <div data-testid="pseq2-rating-legend" className="flex items-start gap-3 sm:gap-4">
        {p.rating.levels.map((lv, i) => (
          <div key={i} className="flex w-14 flex-col items-center gap-1.5">
            <span
              className="h-3 w-3 rounded-full transition-all duration-500"
              style={{
                background: RATING_COLORS[i],
                opacity: rIdx === i ? 1 : 0.22,
                boxShadow: rIdx === i ? `0 0 14px ${RATING_COLORS[i]}` : "none",
                transform: rIdx === i ? "scale(1.25)" : "scale(1)",
              }}
            />
            <span className={`text-center font-mono text-[9px] uppercase leading-tight tracking-wider ${rIdx === i ? "text-bone" : "text-bone/35"}`}>
              {lv}
            </span>
          </div>
        ))}
      </div>
      <p className="max-w-xs text-center font-mono text-[10px] leading-relaxed text-bone/40">{p.rating.basis}</p>
      {weatherState === "loading" && (
        <p className="animate-pulse font-mono text-xs text-sky-200/80">{p.location.loading}</p>
      )}
      {weatherState === "error" && (
        <p className="font-mono text-xs text-red-400">{p.location.error}</p>
      )}
    </div>
  );
}

function PhotoBg({ markerPos }) {
  const [lat, lng] = markerPos;
  const { x, y } = tileCoords(lat, lng, TILE_Z);
  const tiles = [];
  for (let dy = -1; dy <= 1; dy++) {
    for (let dx = -1; dx <= 1; dx++) {
      tiles.push({ dx, dy });
    }
  }
  return (
    <div className="absolute inset-0 overflow-hidden" aria-hidden="true" data-testid="pseq2-cube-photo">
      <div className="absolute left-1/2 top-1/2 grid h-[768px] w-[768px] -translate-x-1/2 -translate-y-1/2 grid-cols-3">
        {tiles.map((t) => (
          <img
            key={`${t.dx}_${t.dy}`}
            src={tileUrl(x + t.dx, y + t.dy, TILE_Z)}
            alt=""
            crossOrigin="anonymous"
            className="h-64 w-64 object-cover"
          />
        ))}
      </div>
      <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/40 to-slate-950/55" />
    </div>
  );
}

function CapacityBars({ p, raw, weatherState }) {
  const items = [
    { label: "PSEQ2", v: raw.pseq2 },
    { label: "CO₂", v: Number(raw.co2) },
    { label: p.cubes.feed.label, v: raw.feed },
    { label: p.cubes.jobs.label, v: raw.jobs },
  ];
  const max = Math.max(...items.map((i) => i.v), 1);
  return (
    <div
      data-testid="pseq2-capacity-bars"
      className="absolute inset-x-4 bottom-4 rounded-2xl border border-sky-200/20 bg-slate-950/55 px-4 py-3 backdrop-blur-md"
    >
      <div className="flex items-end justify-around gap-3">
        {items.map((it, i) => (
          <div key={i} className="flex w-16 flex-col items-center gap-1.5">
            <span className="font-mono text-[10px] font-bold text-sky-100">
              {weatherState === "loading" ? "…" : it.v.toLocaleString()}
            </span>
            <motion.div
              className="w-8 rounded-t-md"
              style={{
                background: `linear-gradient(180deg, ${BAR_COLORS[i]} 0%, rgba(125,211,252,0.12) 100%)`,
                boxShadow: `0 0 12px ${BAR_COLORS[i]}55`,
              }}
              initial={{ height: 4 }}
              animate={{ height: Math.max(6, (it.v / max) * 88) }}
              transition={{ type: "spring", stiffness: 90, damping: 16 }}
            />
            <span className="max-w-[64px] truncate font-mono text-[8px] uppercase tracking-[0.1em] text-sky-200/60">
              {it.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

const previewItem = {
  hidden: { opacity: 0, y: -22, scale: 0.98 },
  show: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.45, ease: EASE } },
};

function PreviewRow({ label, value }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1">
      <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-ink/50">{label}</span>
      <span className="text-right font-mono text-xs font-bold text-deep">{value}</span>
    </div>
  );
}

function ReportPreview({ p, bassins, sunLabel, place, markerPos, weather, weatherState, results, rIdx, lang }) {
  const sig = [bassins, sunLabel, place, weatherState, results.pseq2].join("|");
  const capacity = [
    { label: p.cubes.pseq2.label, value: results.pseq2, unit: p.cubes.pseq2.unit },
    { label: p.cubes.co2.label, value: results.co2, unit: p.cubes.co2.unit },
    { label: p.cubes.feed.label, value: results.feed, unit: p.cubes.feed.unit },
    { label: p.cubes.jobs.label, value: results.jobs, unit: p.cubes.jobs.unit },
  ];
  const weatherCells = [
    { l: p.weather.temp, v: weather ? `${weather.temp.toFixed(1)}°C` : "--" },
    { l: p.weather.humidity, v: weather ? `${Math.round(weather.humidity)}%` : "--" },
    { l: p.weather.pressure, v: weather ? `${Math.round(weather.pressure)} hPa` : "--" },
    { l: p.weather.sunshine, v: weather ? `${weather.sunshine.toFixed(1)}${p.weather.hours}` : "--" },
  ];
  return (
    <motion.div
      key={sig}
      data-testid="pseq2-pdf-preview"
      initial="hidden"
      animate="show"
      variants={{ hidden: {}, show: { transition: { staggerChildren: 0.09, delayChildren: 0.05 } } }}
      className="mx-auto w-full max-w-xl rounded-2xl bg-white p-6 text-left shadow-2xl shadow-black/40 ring-1 ring-deep/10 sm:p-8"
    >
      <motion.div variants={previewItem} className="flex items-center justify-between gap-4 border-b-2 border-sky-400 pb-3">
        <div>
          <p className="font-headline text-sm font-extrabold uppercase tracking-[0.18em] text-deep">Dekrbat Pure</p>
          <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.2em] text-sky-600">{p.preview.title}</p>
        </div>
        <p className="shrink-0 font-mono text-[10px] text-ink/40">{new Date().toLocaleDateString(lang)}</p>
      </motion.div>

      <motion.div variants={previewItem} className="mt-5">
        <p className="font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-sky-700">{p.preview.config}</p>
        <div className="mt-2 divide-y divide-lined/60">
          <PreviewRow label={p.config.bassins} value={bassins.toLocaleString()} />
          <PreviewRow label={p.config.totalSurface} value={`${(bassins * BASSIN_M2).toLocaleString()} m²`} />
          <PreviewRow label={p.config.sun} value={sunLabel} />
          <PreviewRow
            label={p.location.title}
            value={place || (markerPos ? `${markerPos[0].toFixed(2)}°, ${markerPos[1].toFixed(2)}°` : "-")}
          />
        </div>
        <p className="mt-2 font-mono text-[9px] leading-relaxed text-ink/40">{p.config.surfaceFixed}</p>
      </motion.div>

      <motion.div variants={previewItem} className="mt-5">
        <p className="font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-sky-700">{p.preview.weather}</p>
        <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
          {weatherCells.map((w2, i) => (
            <div key={i} className="rounded-lg border border-sky-200/70 bg-sky-50/70 px-2.5 py-2 text-center">
              <p className="font-mono text-xs font-bold text-sky-900">{weatherState === "loading" ? "…" : w2.v}</p>
              <p className="mt-0.5 font-mono text-[8px] uppercase tracking-[0.12em] text-sky-700/60">{w2.l}</p>
            </div>
          ))}
        </div>
      </motion.div>

      <motion.div variants={previewItem} className="mt-5" data-testid="pseq2-preview-capacity">
        <div className="flex items-center justify-between gap-3">
          <p className="font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-sky-700">{p.preview.capacity}</p>
          {rIdx >= 0 && (
            <span
              className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-[0.14em]"
              style={{ borderColor: RATING_COLORS[rIdx], color: RATING_COLORS[rIdx] }}
            >
              {p.rating.levels[rIdx]}
            </span>
          )}
        </div>
        <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
          {capacity.map((c, i) => (
            <div
              key={i}
              className="rounded-lg border border-sky-300/60 bg-gradient-to-br from-sky-100/90 to-sky-200/50 px-2.5 py-3 text-center"
              style={{ boxShadow: "inset 0 0 18px rgba(125,211,252,0.35)" }}
            >
              <p className="font-mono text-base font-bold text-sky-900">{weatherState === "loading" ? "…" : c.value}</p>
              <p className="mt-1 font-mono text-[8px] uppercase leading-tight tracking-[0.12em] text-sky-800/60">{c.label}</p>
              <p className="font-mono text-[8px] text-sky-700/50">{c.unit}</p>
            </div>
          ))}
        </div>
      </motion.div>

      <motion.div
        variants={previewItem}
        data-testid="pseq2-preview-contamination"
        className="mt-5 rounded-xl border border-sky-300/50 bg-sky-50/80 p-4"
      >
        <p className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-sky-700">
          {p.preview.contaminationTitle}
        </p>
        <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-ink/60">{p.preview.contaminationText}</p>
      </motion.div>

      <motion.p variants={previewItem} className="mt-5 border-t border-lined pt-3 font-mono text-[9px] leading-relaxed text-ink/40">
        {p.disclaimer}
      </motion.p>
    </motion.div>
  );
}

export function Pseq2Teaser() {
  const { t } = useLang();
  const p = t.pseq2.teaser;
  return (
    <section className="emerald-mesh grain-overlay relative overflow-hidden">
      <div className="grid-pattern absolute inset-0" aria-hidden="true" />
      <div className="hero-glow absolute inset-0" aria-hidden="true" />
      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-4 py-20 sm:px-6 sm:py-24 lg:grid-cols-2 lg:px-8">
        <Reveal>
          <Overline>{p.overline}</Overline>
          <h2 className="mt-4 font-headline text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">
            {p.title}
          </h2>
          <p className="mt-4 max-w-md text-base leading-relaxed text-ink/70">{p.desc}</p>
          <Link
            data-testid="pseq2-teaser-cta"
            to="/pseq2"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-teal px-7 py-3.5 font-headline text-sm font-bold text-white transition-colors duration-300 hover:bg-deep"
          >
            {p.btn}
            <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
        <Reveal delay={0.15} className="flex justify-center">
          <HoloCube value="PSEQ2" unit={t.pseq2.cubes.pseq2.unit} label={t.pseq2.cubes.pseq2.label} testid="pseq2-teaser-cube" size={170} />
        </Reveal>
      </div>
    </section>
  );
}

const inputCls =
  "w-full rounded-xl border border-lined bg-white/80 px-4 py-3 text-sm text-ink outline-none transition-colors duration-300 focus:border-teal";

export default function Pseq2() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const p = t.pseq2;
  const [searchParams] = useSearchParams();
  const q = (k) => searchParams.get(k);
  const numParam = (v, min, max, dflt) => {
    const n = Number(v);
    return v !== null && Number.isFinite(n) && n >= min && n <= max ? n : dflt;
  };

  const [bassins, setBassins] = useState(() => numParam(q("b"), 1, 1000000, 6));
  const [bassinsText, setBassinsText] = useState(null);
  const [sun, setSun] = useState(() => (["full", "mid", "low"].includes(q("sun")) ? q("sun") : "mid"));
  const [satellite, setSatellite] = useState(false);
  const [locating, setLocating] = useState(false);
  const [tourRun, setTourRun] = useState(false);

  const closeTour = () => {
    setTourRun(false);
    localStorage.setItem(PSEQ2_TOUR_KEY, "done");
  };

  // auto-start the guided tour on first visit
  useEffect(() => {
    if (localStorage.getItem(PSEQ2_TOUR_KEY)) return;
    const t = setTimeout(() => setTourRun(true), 1600);
    return () => clearTimeout(t);
  }, []);
  const [markerPos, setMarkerPos] = useState(() => {
    const la = q("lat");
    const ln = q("lng");
    if (!la || !ln) return null;
    const lat = Number(la);
    const lng = Number(ln);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
    return [lat, lng];
  });
  const [place, setPlace] = useState(() => (q("place") || "").slice(0, 80));
  const [weather, setWeather] = useState(null);
  const [weatherState, setWeatherState] = useState("idle");
  const [pdfBusy, setPdfBusy] = useState(false);
  const [saveBusy, setSaveBusy] = useState(false);
  const [runs, setRuns] = useState([]);
  const [historyFilter, setHistoryFilter] = useState("all");

  const loadRuns = () => {
    fetch(`${API}/api/pseq2-runs`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then((b) => setRuns(Array.isArray(b) ? b : []))
      .catch(() => {});
  };

  useEffect(() => {
    loadRuns();
  }, []);

  useEffect(() => {
    if (!markerPos) return;
    const ctrl = new AbortController();
    const [lat, lng] = markerPos;
    setWeatherState("loading");
    fetch(
      `https://api.open-meteo.com/v1/forecast?latitude=${lat.toFixed(4)}&longitude=${lng.toFixed(4)}&current=temperature_2m,relative_humidity_2m,surface_pressure&daily=sunshine_duration&timezone=auto&forecast_days=1`,
      { signal: ctrl.signal }
    )
      .then((r) => r.json())
      .then((b) => {
        if (b.error || !b.current) throw new Error("no data");
        setWeather({
          temp: b.current.temperature_2m,
          humidity: b.current.relative_humidity_2m,
          pressure: b.current.surface_pressure,
          sunshine: (b.daily?.sunshine_duration?.[0] ?? 0) / 3600,
        });
        setWeatherState("ok");
      })
      .catch((e) => {
        if (e.name !== "AbortError") {
          setWeather(null);
          setWeatherState("error");
        }
      });
    return () => ctrl.abort();
  }, [markerPos]);

  const score = scoreSite(weather);
  const rIdx = ratingIndex(score);
  const weatherFactor = score == null ? 1 : 0.5 + (score / 100) * 0.8;
  const units = bassins * (BASSIN_M2 / 100) * SUN_FACTORS[sun] * weatherFactor;
  const raw = {
    pseq2: Math.round(units * 12),
    co2: (units * 3).toFixed(1),
    feed: Math.round(units * 10),
    jobs: Math.max(1, Math.round(units * 0.8)),
  };
  const results = {
    pseq2: raw.pseq2.toLocaleString(),
    co2: raw.co2,
    feed: raw.feed.toLocaleString(),
    jobs: raw.jobs.toLocaleString(),
  };

  const sliderMax = Math.max(50, Math.ceil(bassins / 50) * 50);
  const sunLabel = { full: p.config.sunFull, mid: p.config.sunMid, low: p.config.sunLow }[sun];
  const visibleRuns = historyFilter === "public" ? runs.filter((r) => r.shared) : runs;

  const onMapPick = ([lat, lng]) => {
    setMarkerPos([lat, lng]);
    setPlace(`${lat.toFixed(2)}°, ${lng.toFixed(2)}°`);
  };

  const locateMe = () => {
    if (!navigator.geolocation || locating) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false);
        onMapPick([pos.coords.latitude, pos.coords.longitude]);
      },
      () => setLocating(false),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const shareLink = () => {
    const params = new URLSearchParams({ b: String(bassins), sun });
    if (markerPos) {
      params.set("lat", markerPos[0].toFixed(4));
      params.set("lng", markerPos[1].toFixed(4));
    }
    if (place) params.set("place", place);
    // share the OG card route so WhatsApp/social scrapers get logo + zone photo; humans are redirected to the live simulator
    const url = `${API}/api/share-card?${params.toString()}`;
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(
        () => toast.success(p.config.shareCopied),
        () => toast(url)
      );
    } else {
      toast(url);
    }
  };

  const saveScenario = async () => {
    setSaveBusy(true);
    try {
      const body = {
        bassins,
        sun,
        place: place || "",
        lat: markerPos ? markerPos[0] : null,
        lng: markerPos ? markerPos[1] : null,
        temp: weather ? weather.temp : null,
        humidity: weather ? weather.humidity : null,
        pressure: weather ? weather.pressure : null,
        sunshine: weather ? weather.sunshine : null,
        score,
        pseq2: raw.pseq2,
        co2: Number(raw.co2),
        feed: raw.feed,
        jobs: raw.jobs,
      };
      const r = await fetch(`${API}/api/pseq2-runs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error("save failed");
      toast.success(p.history.saved);
      loadRuns();
    } catch {
      toast.error(p.history.error);
    } finally {
      setSaveBusy(false);
    }
  };

  const loadScenario = (r) => {
    setBassins(r.bassins);
    setBassinsText(null);
    setSun(r.sun);
    if (r.lat != null && r.lng != null) setMarkerPos([r.lat, r.lng]);
    setPlace(r.place || "");
    window.lenis?.scrollTo(0, { duration: 0.8 });
  };

  const deleteScenario = async (id) => {
    try {
      const r = await fetch(`${API}/api/pseq2-runs/${id}`, { method: "DELETE", credentials: "include" });
      if (!r.ok) throw new Error("delete failed");
      setRuns((prev) => prev.filter((x) => x._id !== id));
    } catch {
      toast.error(p.history.error);
    }
  };

  const exportHistoryPdf = async () => {
    const pdfLang = ["es", "en", "pt", "fr"].includes(lang) ? lang : "en";
    const hp = dictionary[pdfLang].pseq2;
    const rt = hp.report;
    const locale = { es: "es-SV", pt: "pt-BR", fr: "fr-FR", en: "en-US" }[pdfLang];
    const doc = new jsPDF();
    let logoData = null;
    try {
      logoData = await fetch("/dekrbat-logo-deep.png")
        .then((r) => r.blob())
        .then(
          (b) =>
            new Promise((res) => {
              const fr = new FileReader();
              fr.onload = () => res(fr.result);
              fr.readAsDataURL(b);
            })
        );
      doc.addImage(logoData, "PNG", 14, 12, 52, 7.7);
    } catch {}
    // holographic brand watermark
    try {
      if (logoData) {
        doc.setGState(new doc.GState({ opacity: 0.05 }));
        for (let i = 0; i < 3; i++) doc.addImage(logoData, "PNG", 55, 80 + i * 70, 100, 14.8);
        doc.setGState(new doc.GState({ opacity: 1 }));
      }
    } catch {}
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor(3, 105, 161);
    doc.text(hp.history.title, 14, 36);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(90, 105, 98);
    doc.text(`${rt.date}: ${new Date().toLocaleDateString(locale)}`, 14, 43);
    doc.setDrawColor(56, 189, 248);
    doc.setLineWidth(0.8);
    doc.line(14, 47, 196, 47);

    const cols = [hp.history.date, hp.location.title, hp.config.bassins, hp.weather.temp, "PSEQ2", "CO2", hp.cubes.feed.label, hp.cubes.jobs.label];
    const xs = [14, 40, 92, 112, 130, 150, 168, 188];
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    doc.setTextColor(3, 105, 161);
    cols.forEach((c, i) => doc.text(String(c).toUpperCase().slice(0, 16), xs[i], 55));
    doc.setDrawColor(186, 230, 253);
    doc.setLineWidth(0.3);
    doc.line(14, 57, 196, 57);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    visibleRuns.slice(0, 26).forEach((r, idx) => {
      const y = 63 + idx * 7.5;
      if (idx % 2 === 0) {
        doc.setFillColor(240, 249, 255);
        doc.rect(14, y - 4.6, 182, 7.5, "F");
      }
      doc.setTextColor(15, 23, 42);
      const row = [
        new Date(r.created_at).toLocaleDateString(locale),
        (r.place || (r.lat != null ? `${r.lat.toFixed(1)}, ${r.lng.toFixed(1)}` : "-")).slice(0, 26),
        String(r.bassins),
        r.temp != null ? `${r.temp.toFixed(1)}C` : "-",
        String(r.pseq2),
        String(r.co2),
        String(r.feed),
        String(r.jobs),
      ];
      row.forEach((c, i) => doc.text(c, xs[i], y));
    });

    doc.save(pdfLang === "es" ? "escenarios-pseq2.pdf" : pdfLang === "pt" ? "cenarios-pseq2.pdf" : pdfLang === "fr" ? "scenarios-pseq2.pdf" : "pseq2-scenarios.pdf");
  };

  const toggleShare = async (r) => {
    try {
      const res = await fetch(`${API}/api/pseq2-runs/${r._id}/share`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ shared: !r.shared }),
      });
      if (!res.ok) throw new Error("share failed");
      loadRuns();
    } catch {
      toast.error(p.history.error);
    }
  };

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const pdfLang = ["es", "en", "pt", "fr"].includes(lang) ? lang : "en";
      const rt = dictionary[pdfLang].pseq2.report;
      const levels = dictionary[pdfLang].pseq2.rating.levels;
      const doc = new jsPDF();
      const toDataURL = (b) =>
        new Promise((res) => {
          const fr = new FileReader();
          fr.onload = () => res(fr.result);
          fr.readAsDataURL(b);
        });
      const logoData = await fetch("/dekrbat-logo-deep.png").then((r) => r.blob()).then(toDataURL);
      doc.addImage(logoData, "PNG", 14, 12, 52, 7.7);
      // holographic brand watermark
      try {
        doc.setGState(new doc.GState({ opacity: 0.05 }));
        for (let i = 0; i < 3; i++) doc.addImage(logoData, "PNG", 55, 80 + i * 70, 100, 14.8);
        doc.setGState(new doc.GState({ opacity: 1 }));
      } catch {}
      doc.setFont("helvetica", "bold");
      doc.setFontSize(18);
      doc.setTextColor(6, 35, 26);
      doc.text(rt.title, 14, 40);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(90, 105, 98);
      doc.text(`${rt.date}: ${new Date().toLocaleDateString({ es: "es-SV", pt: "pt-BR", fr: "fr-FR", en: "en-US" }[pdfLang])}`, 14, 47);
      doc.setDrawColor(56, 189, 248);
      doc.setLineWidth(0.8);
      doc.line(14, 52, 196, 52);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.setTextColor(3, 105, 161);
      doc.text(rt.config, 14, 64);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(17, 24, 28);
      const configLines = [
        `${rt.bassins}: ${bassins}`,
        rt.surfaceFixed,
        `${rt.totalSurface}: ${bassins * BASSIN_M2}`,
        `${rt.sun}: ${sunLabel}`,
        `${rt.locationT}: ${place || (markerPos ? `${markerPos[0].toFixed(2)}, ${markerPos[1].toFixed(2)}` : "-")}`,
      ];
      configLines.forEach((line, i) => doc.text(line, 14, 73 + i * 6.5));

      let y = 73 + configLines.length * 6.5 + 8;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.setTextColor(3, 105, 161);
      doc.text(rt.weatherT, 14, y);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(17, 24, 28);
      const weatherLines = weather
        ? [
            `${p.weather.temp}: ${weather.temp.toFixed(1)} C`,
            `${p.weather.humidity}: ${Math.round(weather.humidity)} %`,
            `${p.weather.pressure}: ${Math.round(weather.pressure)} hPa`,
            `${p.weather.sunshine}: ${weather.sunshine.toFixed(1)} h`,
          ]
        : ["-"];
      weatherLines.forEach((line, i) => doc.text(line, 14, y + 9 + i * 6.5));
      y += 9 + weatherLines.length * 6.5 + 8;

      if (rIdx >= 0) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        doc.setTextColor(3, 105, 161);
        doc.text(`${rt.ratingT}: ${levels[rIdx]}`, 14, y);
        y += 12;
      }

      // Holographic light-blue capacity boxes (cube faces summary)
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.setTextColor(3, 105, 161);
      doc.text(rt.capacity, 14, y);
      const boxW = 44;
      const gap = 2;
      const caps = [
        { v: String(raw.pseq2), l: "PSEQ2" },
        { v: String(raw.co2), l: "CO2" },
        { v: String(raw.feed), l: p.cubes.feed.label },
        { v: String(raw.jobs), l: p.cubes.jobs.label },
      ];
      caps.forEach((c, i) => {
        const x = 14 + i * (boxW + gap);
        doc.setFillColor(224, 242, 254);
        doc.setDrawColor(125, 211, 252);
        doc.setLineWidth(0.4);
        doc.roundedRect(x, y + 6, boxW, 22, 2, 2, "FD");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.setTextColor(12, 74, 110);
        doc.text(c.v, x + boxW / 2, y + 14.5, { align: "center" });
        doc.setFont("helvetica", "normal");
        doc.setFontSize(6);
        doc.setTextColor(3, 105, 161);
        const lbl = doc.splitTextToSize(c.l.toUpperCase(), boxW - 4);
        doc.text(lbl.slice(0, 2), x + boxW / 2, y + 20, { align: "center" });
      });
      y += 36;

      const drawContamination = (yy) => {
        doc.setFillColor(224, 242, 254);
        doc.setDrawColor(125, 211, 252);
        doc.setLineWidth(0.4);
        doc.roundedRect(14, yy, 182, 34, 2, 2, "FD");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.setTextColor(3, 105, 161);
        doc.text(rt.contaminationTitle, 18, yy + 7.5);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8.5);
        doc.setTextColor(40, 60, 75);
        const contLines = doc.splitTextToSize(rt.contaminationText, 174);
        doc.text(contLines, 18, yy + 14);
      };

      if (markerPos) {
        // Site photo + bar chart on a dedicated page
        doc.addPage();
        let y2 = 20;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        doc.setTextColor(3, 105, 161);
        doc.text(`${rt.locationT}: ${place || `${markerPos[0].toFixed(2)}, ${markerPos[1].toFixed(2)}`}`, 14, y2);
        y2 += 6;
        try {
          const tc = tileCoords(markerPos[0], markerPos[1], TILE_Z);
          const imgData = await fetch(tileUrl(tc.x, tc.y, TILE_Z)).then((r) => r.blob()).then(toDataURL);
          doc.addImage(imgData, "JPEG", 14, y2, 88, 88);
        } catch {}
        const bars = [
          { v: raw.pseq2, l: "PSEQ2" },
          { v: Number(raw.co2), l: "CO2" },
          { v: raw.feed, l: p.cubes.feed.label },
          { v: raw.jobs, l: p.cubes.jobs.label },
        ];
        const maxBar = Math.max(...bars.map((b) => b.v), 1);
        const bx0 = 112;
        const bw = 17;
        const bgap = 5.67;
        const rgb = [[125, 211, 252], [56, 189, 248], [14, 165, 233], [2, 132, 199]];
        bars.forEach((b, i) => {
          const h = Math.max(2, (b.v / maxBar) * 68);
          const x = bx0 + i * (bw + bgap);
          doc.setFillColor(rgb[i][0], rgb[i][1], rgb[i][2]);
          doc.rect(x, y2 + 78 - h, bw, h, "F");
          doc.setFont("helvetica", "bold");
          doc.setFontSize(7);
          doc.setTextColor(12, 74, 110);
          doc.text(String(b.v), x + bw / 2, y2 + 76 - h, { align: "center" });
          doc.setFont("helvetica", "normal");
          doc.setFontSize(5.5);
          doc.setTextColor(3, 105, 161);
          const bl = doc.splitTextToSize(b.l.toUpperCase(), bw + 6);
          doc.text(bl.slice(0, 2), x + bw / 2, y2 + 82, { align: "center" });
        });
        drawContamination(y2 + 96);
      } else {
        drawContamination(y + 2);
      }

      doc.setFontSize(8);
      doc.setTextColor(120, 130, 125);
      const disclaimerLines = doc.splitTextToSize(rt.footer, 180);
      doc.text(disclaimerLines, 14, 280);

      doc.save(pdfLang === "es" ? "informe-pseq2.pdf" : pdfLang === "pt" ? "relatorio-pseq2.pdf" : pdfLang === "fr" ? "rapport-pseq2.pdf" : "pseq2-report.pdf");
    } finally {
      setPdfBusy(false);
    }
  };

  const whatIcons = [Radar, Cpu, Coins];

  return (
    <>
      <section data-testid="pseq2-page" className="emerald-mesh grain-overlay relative overflow-hidden pb-16 pt-20 sm:pt-28">
        <div className="grid-pattern absolute inset-0" aria-hidden="true" />
        <div className="hero-glow absolute inset-0" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Overline>{p.overline}</Overline>
            <h1 data-testid="pseq2-hero-title" className="mt-4 max-w-3xl font-headline text-4xl font-extrabold leading-[1.08] tracking-tight text-ink sm:text-5xl">
              {p.title}
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-ink/70 sm:text-lg">{p.sub}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-gradient-to-b from-sky-100/70 via-bone to-bone pb-24">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-4 sm:px-6 lg:grid-cols-5 lg:px-8">
          <Reveal className="lg:col-span-2">
            <div className="rounded-3xl border border-lined bg-white/80 p-7 shadow-xl shadow-sky-900/5 backdrop-blur-md sm:p-9">
              <div className="flex items-center gap-3">
                <Box className="h-5 w-5 text-teal" />
                <h2 className="font-headline text-lg font-bold text-ink">{p.config.title}</h2>
                <button
                  data-testid="pseq2-tour-replay"
                  onClick={() => setTourRun(true)}
                  className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-lined bg-white/85 px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.12em] text-ink/50 transition-colors duration-300 hover:text-teal"
                >
                  <HelpCircle className="h-3 w-3" />
                  {p.tour.replay}
                </button>
              </div>

              <div className="mt-8">
                <label htmlFor="pseq2-bassins" className="flex items-baseline justify-between font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-ink/60">
                  <span>{p.config.bassins}</span>
                  <span className="font-headline text-2xl font-extrabold text-teal">{bassins.toLocaleString()}</span>
                </label>
                <input
                  data-testid="pseq2-config-bassins-slider"
                  id="pseq2-bassins"
                  type="range"
                  min="1"
                  max={sliderMax}
                  value={Math.min(bassins, sliderMax)}
                  onChange={(e) => {
                    setBassins(Number(e.target.value));
                    setBassinsText(null);
                  }}
                  className="mt-3 w-full"
                />
                <div className="mt-3 flex items-center gap-3">
                  <input
                    data-testid="pseq2-config-bassins-input"
                    type="number"
                    min="1"
                    value={bassinsText ?? bassins}
                    onChange={(e) => {
                      const v = e.target.value;
                      setBassinsText(v);
                      const n = Math.floor(Number(v));
                      if (v.trim() !== "" && Number.isFinite(n) && n >= 1) setBassins(n);
                    }}
                    onBlur={() => setBassinsText(null)}
                    className={`${inputCls} max-w-[8rem] shrink-0`}
                    aria-label={p.config.bassins}
                  />
                  <p className="font-mono text-[10px] leading-relaxed text-ink/40">{p.config.bassinsFree}</p>
                </div>
              </div>

              <div className="mt-7 rounded-xl border border-teal/25 bg-teal/5 p-4">
                <p className="font-mono text-[11px] uppercase leading-relaxed tracking-[0.14em] text-ink/60">
                  {p.config.surfaceFixed}
                </p>
                <p className="mt-2 font-headline text-xl font-extrabold text-teal">
                  {(bassins * BASSIN_M2).toLocaleString()} m²
                  <span className="ml-2 font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-ink/50">
                    · {p.config.totalSurface}
                  </span>
                </p>
              </div>

              <div className="mt-7">
                <label htmlFor="pseq2-sun" className="mb-2 block font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-ink/60">
                  {p.config.sun}
                </label>
                <select
                  data-testid="pseq2-config-sun-select"
                  id="pseq2-sun"
                  value={sun}
                  onChange={(e) => setSun(e.target.value)}
                  className={inputCls}
                >
                  <option value="full">{p.config.sunFull}</option>
                  <option value="mid">{p.config.sunMid}</option>
                  <option value="low">{p.config.sunLow}</option>
                </select>
              </div>

              <div className="mt-7">
                <label className="mb-2 block font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-ink/60">
                  {p.location.title}
                </label>
                <div data-testid="pseq2-map" className="relative overflow-hidden rounded-2xl border border-lined">
                  <div className="absolute right-2 top-2 z-[1000] flex flex-col gap-1.5">
                    <button
                      data-testid="pseq2-map-satellite-toggle"
                      onClick={() => setSatellite((v) => !v)}
                      aria-pressed={satellite}
                      className={`rounded-full border px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.12em] backdrop-blur-md transition-colors duration-300 ${
                        satellite ? "border-sky-600 bg-sky-600/90 text-white" : "border-lined bg-white/85 text-ink/60 hover:text-teal"
                      }`}
                    >
                      {satellite ? p.location.mapLabel : p.location.satellite}
                    </button>
                    <button
                      data-testid="pseq2-map-locate-btn"
                      onClick={locateMe}
                      disabled={locating}
                      className="inline-flex items-center gap-1.5 rounded-full border border-lined bg-white/85 px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.12em] text-ink/60 backdrop-blur-md transition-colors duration-300 hover:text-teal disabled:opacity-60"
                    >
                      {locating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Crosshair className="h-3 w-3" />}
                      {p.location.locate}
                    </button>
                  </div>
                  <MapContainer
                    center={[20, -15]}
                    zoom={2}
                    scrollWheelZoom={false}
                    style={{ height: "260px", width: "100%" }}
                  >
                    <TileLayer
                      key={satellite ? "sat" : "osm"}
                      attribution={
                        satellite
                          ? "Esri, Maxar, Earthstar Geographics"
                          : '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      }
                      url={
                        satellite
                          ? "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                          : "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      }
                    />
                    <ClickPicker onPick={onMapPick} />
                    <FlyTo pos={markerPos} />
                    {markerPos && <Marker position={markerPos} icon={dotIcon} />}
                  </MapContainer>
                </div>
                <p className="mt-2 font-mono text-[10px] text-ink/40">
                  {place ? `📍 ${place}` : p.location.hint}
                </p>
              </div>

              <div className="mt-8 flex flex-col gap-3">
                <button
                  data-testid="pseq2-report-btn"
                  onClick={downloadPdf}
                  disabled={pdfBusy}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-teal px-6 py-3.5 font-headline text-sm font-bold text-white transition-colors duration-300 hover:bg-deep disabled:opacity-60"
                >
                  <Download className="h-4 w-4" />
                  {p.report?.btn || dictionary.en.pseq2.report.btn}
                </button>
                <button
                  data-testid="pseq2-share-btn"
                  onClick={shareLink}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-teal/40 px-6 py-3.5 font-headline text-sm font-semibold text-teal transition-colors duration-300 hover:bg-teal/10"
                >
                  <Share2 className="h-4 w-4" />
                  {p.config.share}
                </button>
                <PSeQ2XrButton bassins={bassins} sun={sun} lat={markerPos?.[0]} lng={markerPos?.[1]} place={place} />
                <button
                  data-testid="pseq2-history-save-btn"
                  onClick={saveScenario}
                  disabled={saveBusy}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-ink/15 px-6 py-3.5 font-headline text-sm font-semibold text-ink/80 transition-colors duration-300 hover:border-teal hover:text-teal disabled:opacity-60"
                >
                  <Save className="h-4 w-4" />
                  {p.history.save}
                </button>
              </div>

              <p className="mt-6 rounded-xl bg-sky-100/70 p-4 font-mono text-[11px] leading-relaxed text-ink/50">
                {p.disclaimer}
              </p>
            </div>
          </Reveal>

          <div className="flex flex-col items-center justify-center gap-14 py-10 lg:col-span-3">
            <Reveal delay={0.1} className="w-full">
              <div className="relative mx-auto w-full max-w-xl overflow-hidden rounded-3xl border border-sky-300/50 bg-gradient-to-br from-slate-950 via-sky-950 to-slate-900 shadow-2xl shadow-sky-900/20">
                {markerPos && <PhotoBg markerPos={markerPos} />}
                <div className="relative flex justify-center px-6 pb-32 pt-10">
                  <MasterCube
                    p={p}
                    place={place}
                    weather={weather}
                    weatherState={weatherState}
                    results={results}
                    rIdx={rIdx}
                  />
                </div>
                <CapacityBars p={p} raw={raw} weatherState={weatherState} />
              </div>
            </Reveal>
            <Reveal delay={0.2} className="w-full">
              <ReportPreview
                p={p}
                bassins={bassins}
                sunLabel={sunLabel}
                place={place}
                markerPos={markerPos}
                weather={weather}
                weatherState={weatherState}
                results={results}
                rIdx={rIdx}
                lang={lang}
              />
            </Reveal>
          </div>
        </div>

        <div className="mx-auto mt-16 max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div data-testid="pseq2-history" className="rounded-3xl border border-lined bg-white/80 p-7 shadow-xl shadow-sky-900/5 backdrop-blur-md sm:p-9">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <History className="h-5 w-5 text-teal" />
                  <h2 className="font-headline text-lg font-bold text-ink">{p.history.title}</h2>
                </div>
                {runs.length > 0 && (
                  <button
                    data-testid="pseq2-history-pdf-btn"
                    onClick={exportHistoryPdf}
                    className="inline-flex items-center gap-2 rounded-full border border-teal/40 px-4 py-2 font-headline text-xs font-semibold text-teal transition-colors duration-300 hover:bg-teal hover:text-white"
                  >
                    <FileDown className="h-3.5 w-3.5" />
                    {p.history.exportPdf}
                  </button>
                )}
              </div>
              {runs.length > 0 && (
                <div className="mt-5 inline-flex rounded-full border border-lined p-1">
                  {["all", "public"].map((f) => (
                    <button
                      key={f}
                      data-testid={`pseq2-history-tab-${f}`}
                      onClick={() => setHistoryFilter(f)}
                      className={`rounded-full px-4 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.14em] transition-colors duration-200 ${
                        historyFilter === f ? "bg-teal text-white" : "text-ink/50 hover:text-teal"
                      }`}
                    >
                      {f === "all" ? p.history.tabAll : p.history.tabPublic}
                    </button>
                  ))}
                </div>
              )}
              {visibleRuns.length === 0 ? (
                <p data-testid="pseq2-history-empty" className="mt-6 font-mono text-xs leading-relaxed text-ink/45">
                  {p.history.empty}
                </p>
              ) : (
                <div className="mt-6 overflow-x-auto">
                  <table className="w-full min-w-[760px] text-left">
                    <thead>
                      <tr className="border-b border-lined font-mono text-[10px] uppercase tracking-[0.16em] text-ink/45">
                        <th className="pb-3 pr-4 font-semibold">{p.history.date}</th>
                        <th className="pb-3 pr-4 font-semibold">{p.location.title}</th>
                        <th className="pb-3 pr-4 font-semibold">{p.config.bassins}</th>
                        <th className="pb-3 pr-4 font-semibold">{p.weather.temp}</th>
                        <th className="pb-3 pr-4 font-semibold">PSEQ2</th>
                        <th className="pb-3 pr-4 font-semibold">CO₂</th>
                        <th className="pb-3 pr-4 font-semibold">{p.cubes.feed.label}</th>
                        <th className="pb-3 pr-4 font-semibold">{p.cubes.jobs.label}</th>
                        <th className="pb-3" />
                      </tr>
                    </thead>
                    <tbody>
                      {visibleRuns.map((r) => (
                        <tr
                          key={r._id}
                          data-testid={`pseq2-history-row-${r._id}`}
                          onClick={() => loadScenario(r)}
                          className="cursor-pointer border-b border-lined/70 transition-colors duration-200 hover:bg-sky-50"
                        >
                          <td className="py-3 pr-4 font-mono text-[11px] text-ink/60">
                            {new Date(r.created_at).toLocaleString(lang, { dateStyle: "short", timeStyle: "short" })}
                          </td>
                          <td className="py-3 pr-4 text-xs text-ink/85">
                            {r.place || (r.lat != null ? `${r.lat.toFixed(2)}°, ${r.lng.toFixed(2)}°` : "-")}
                          </td>
                          <td className="py-3 pr-4 font-mono text-xs font-bold text-teal">{r.bassins.toLocaleString()}</td>
                          <td className="py-3 pr-4 font-mono text-xs text-ink/75">{r.temp != null ? `${r.temp.toFixed(1)}°C` : "--"}</td>
                          <td className="py-3 pr-4 font-mono text-xs font-bold text-ink">{r.pseq2.toLocaleString()}</td>
                          <td className="py-3 pr-4 font-mono text-xs text-ink/75">{r.co2}</td>
                          <td className="py-3 pr-4 font-mono text-xs text-ink/75">{r.feed.toLocaleString()}</td>
                          <td className="py-3 pr-4 font-mono text-xs text-ink/75">{r.jobs.toLocaleString()}</td>
                          <td className="py-3 text-right">
                            <div className="flex items-center justify-end gap-1.5">
                              {r.shared && (
                                <span className="inline-flex items-center gap-1 rounded-full bg-teal/10 px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-[0.12em] text-teal">
                                  <Globe className="h-3 w-3" />
                                  {p.history.sharedTag}
                                </span>
                              )}
                              {user && r.user_id === user.user_id && (
                                <button
                                  data-testid={`pseq2-history-share-${r._id}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleShare(r);
                                  }}
                                  className={`rounded-full border p-1.5 transition-colors duration-200 ${
                                    r.shared
                                      ? "border-teal bg-teal/10 text-teal"
                                      : "border-ink/15 text-ink/40 hover:border-teal hover:text-teal"
                                  }`}
                                  aria-label={r.shared ? p.history.unpublish : p.history.publish}
                                  title={r.shared ? p.history.unpublish : p.history.publish}
                                >
                                  <Globe className="h-3.5 w-3.5" />
                                </button>
                              )}
                              {(user ? r.user_id === user.user_id : !r.user_id) && (
                                <button
                                  data-testid={`pseq2-history-delete-${r._id}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deleteScenario(r._id);
                                  }}
                                  className="rounded-full border border-ink/15 p-1.5 text-ink/40 transition-colors duration-200 hover:border-red-400 hover:text-red-400"
                                  aria-label="delete"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-bone py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Overline>{p.what.overline}</Overline>
            <h2 className="mt-4 max-w-2xl font-headline text-2xl font-bold tracking-tight text-deep sm:text-3xl lg:text-4xl">
              {p.what.title}
            </h2>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-ink/65">{p.what.desc}</p>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
            {p.what.cards.map((c, i) => {
              const Icon = whatIcons[i];
              return (
                <Reveal key={i} delay={0.08 * i} className="h-full">
                  <div className="card-glow h-full rounded-3xl border border-lined bg-white p-8 transition-shadow duration-500 hover:shadow-xl hover:shadow-teal/10">
                    <span className="inline-flex rounded-xl bg-teal/10 p-3 text-teal">
                      <Icon className="h-6 w-6" />
                    </span>
                    <h3 className="mt-6 font-headline text-lg font-bold text-deep">{c.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-ink/65">{c.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="emerald-mesh grain-overlay relative overflow-hidden">
        <div className="hero-glow absolute inset-0" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-4 py-20 text-center sm:px-6 lg:px-8">
          <Reveal>
            <h2 className="mx-auto max-w-2xl font-headline text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">
              {p.cta.title}
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-ink/70">{p.cta.desc}</p>
            <Link
              data-testid="pseq2-cta-btn"
              to="/licensing#request-form"
              className="mt-9 inline-flex items-center gap-2 rounded-full bg-teal px-8 py-4 font-headline text-sm font-bold text-white transition-colors duration-300 hover:bg-deep"
            >
              {p.cta.btn}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>
      <GuidedTour
        steps={p.tour.steps.map((s, i) => ({ ...s, target: PSEQ2_TOUR_TARGETS[i] }))}
        labels={p.tour}
        run={tourRun}
        onClose={closeTour}
      />
    </>
  );
}
